/* ============================================
   Shared Utilities — 1Conecta ERP
   Single source of truth for all utility functions
   ============================================ */

/**
 * Round to 2 decimal places for financial precision
 * Prevents IEEE 754 floating point errors (e.g. 0.1 + 0.2)
 */
export const r2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Formatea un número como moneda con SÍMBOLO por delante (S/ 1,234.56 / $1,234.56 /
 * €1,234.56), consistente con el formato manual usado en el resto de la app (UX-04).
 * Monedas desconocidas caen al código detrás (1,234.56 XXX) para no perder la unidad.
 */
const CURRENCY_SYMBOLS: Record<string, string> = { PEN: 'S/ ', USD: '$', EUR: '€' }

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount ?? 0)
  const symbol = CURRENCY_SYMBOLS[currency]
  return symbol ? `${symbol}${formatted}` : `${formatted} ${currency}`
}

/**
 * Format a date string (YYYY-MM-DD) to localized format
 * Source: app.js:19-23 + server.js:160-164
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  // If it's a pure date (YYYY-MM-DD), append time to avoid timezone shift
  // If it's already an ISO timestamp, use as-is
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Formatea un porcentaje para mostrar: redondea a 1 decimal y quita el ".0".
 * Permite guardar un porcentaje con muchos decimales (para que el monto del descuento
 * caiga redondo) y mostrar igual "5%" en el documento.
 */
export function fmtPct(pct: number): string {
  const p = Math.round((pct || 0) * 10) / 10
  return Number.isInteger(p) ? String(p) : p.toFixed(1)
}

/**
 * Determine discount tier based on percentage
 * Source: page-ver-cotizacion.js:7-34 + server.js:165-172
 */
export function getDiscountTier(rawPct: number): {
  label: string
  emoji: string
  color: string
  description: string
} {
  // El tier se decide sobre el porcentaje **mostrado** (redondeado a 1 decimal), no sobre el
  // guardado: un 5.0209% que en pantalla dice "5%" tiene que salir Cortesia, no Preferencial.
  const pct = Math.round((rawPct || 0) * 10) / 10
  if (pct <= 0) return { label: 'Sin descuento', emoji: '', color: '', description: '' }
  if (pct <= 5)
    return {
      label: 'Cortesía',
      emoji: '🤝',
      color: 'var(--success)',
      description: 'Descuento de cortesía por relación comercial',
    }
  if (pct <= 10)
    return {
      label: 'Preferencial',
      emoji: '⭐',
      color: 'var(--info)',
      description: 'Tarifa preferencial para clientes frecuentes',
    }
  if (pct <= 15)
    return {
      label: 'Aliado',
      emoji: '🤝',
      color: 'var(--warning)',
      description: 'Precio especial de aliado estratégico',
    }
  if (pct <= 30)
    return {
      label: 'Partner Agency',
      emoji: '🤝',
      color: 'var(--primary)',
      description: 'Tarifa preferencial para agencias partner',
    }
  return {
    label: 'VIP',
    emoji: '👑',
    color: 'var(--danger)',
    description: 'Descuento máximo exclusivo VIP',
  }
}

/**
 * Generate WhatsApp link
 * Source: server.js:194-198
 */
export function waLink(phone: string, text: string): string {
  const clean = phone.replace(/[^0-9+]/g, '')
  return `https://wa.me/${clean}?text=${encodeURIComponent(text)}`
}

/**
 * Fix mojibake characters caused by UTF-8 bytes misinterpreted as Windows-1252.
 * Common patterns: â€" → —, â€™ → ', â€œ → ", â€ -> "
 */
export function fixMojibake(text: string): string {
  return text
    .replace(/â€"/g, '\u2014')   // em-dash
    .replace(/â€"/g, '\u201C')   // left double quote
    .replace(/â€\u009D/g, '\u201D') // right double quote
    .replace(/â€˜/g, '\u2018')   // left single quote
    .replace(/â€™/g, '\u2019')   // right single quote
    .replace(/â€"/g, '\u2013')   // en-dash
    .replace(/â€"/g, '\u2026')   // ellipsis
    .replace(/Ã±/g, '\u00F1')   // ñ
    .replace(/Ã¡/g, '\u00E1')    // á
    .replace(/Ã©/g, '\u00E9')   // é
    .replace(/Ã­/g, '\u00ED')   // í
    .replace(/Ã³/g, '\u00F3')   // ó
    .replace(/Ãº/g, '\u00FA')   // ú
    .replace(/Ã¼/g, '\u00FC')   // ü
    .replace(/Ã/g, '\u00E1')    // á (single char)
}

/**
 * Process content: convert markdown-like syntax to HTML
 * Source: page-ver-cotizacion.js:50-72 + server.js:173-193
 */
export function processContent(text: string): string {
  if (!text) return ''

  text = fixMojibake(text)

  // Extract mermaid blocks before any other processing
  const mermaidBlocks = parseMermaidBlocks(text)

  if (mermaidBlocks.length > 0) {
    let result = ''
    for (const block of mermaidBlocks) {
      result += processPlainContent(block.before)
      result += `<div class="cot-mermaid-container"><div class="mermaid" data-mermaid-code="${block.code.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}">\n${block.code}\n</div></div>`
    }
    result += processPlainContent(mermaidBlocks[mermaidBlocks.length - 1].after)
    return result
  }

  return processPlainContent(text)
}

/*
  Los colores de aca van literales a proposito: esto arma el HTML del
  DOCUMENTO de la cotizacion, que html2canvas exporta con fondo blanco fijo.
  Un token que se invierta con el tema daria un PDF blanco sobre blanco.
*/
function processPlainContent(text: string): string {
  if (!text) return ''

  // If content already contains HTML elements (from RichTextEditor), return as-is
  if (/<[a-z][\w-]*\b/i.test(text)) {
    return text.replace(/<td([^>]*)>Fase\s+(\d+)<\/td>/gi, '<td$1>F$2</td>')
  }

  return text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()

      if (trimmed.startsWith('### '))
        return `<h5 style="margin:8px 0 4px;color:#1C4587">${trimmed.slice(4)}</h5>`
      if (trimmed.startsWith('## '))
        return `<h4 style="margin:10px 0 5px;color:#1C4587">${trimmed.slice(3)}</h4>`
      if (trimmed.startsWith('# '))
        return `<h3 style="margin:12px 0 6px;color:#1C4587">${trimmed.slice(2)}</h3>`

      const processed = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

      if (trimmed.startsWith('- ') || trimmed.startsWith('* '))
        return `<li style="margin:2px 0;margin-left:20px">${processed.slice(2)}</li>`

      if (!trimmed) return '<br/>'

      return `<p style="margin:4px 0;text-align:justify">${processed}</p>`
    })
    .join('\n')
}

/**
 * Parse mermaid code blocks from markdown content
 * Source: page-ver-cotizacion.js:37-47 + server.js:199-209
 */
export function parseMermaidBlocks(
  text: string
): { before: string; code: string; after: string }[] {
  const blocks: { before: string; code: string; after: string }[] = []

  // Accept both real newlines and literal \n after ```mermaid
  const regex = /```mermaid(?:\s*\n|\\n)([\s\S]*?)```/g
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    // Convert literal \n sequences to real newlines in the mermaid code
    let code = match[1].trim()
    code = code.replace(/\\n/g, '\n')
    blocks.push({
      before: text.slice(lastIndex, match.index),
      code,
      after: '',
    })
    lastIndex = match.index + match[0].length
  }

  if (blocks.length > 0) {
    blocks[blocks.length - 1].after = text.slice(lastIndex)
  }

  return blocks
}

/**
 * Generate next cotización number
 */
export function nextCotNumber(existingNumbers: string[]): string {
  if (existingNumbers.length === 0) return '260001'
  const nums = existingNumbers.map((n) => parseInt(n)).filter((n) => !isNaN(n))
  return String(Math.max(...nums) + 1)
}

/**
 * FTE dedication label for equipo dedicado display
 */
export function fmtFTE(member: { dedicacion_fte: number; horas_custom: number | null }): string {
  if (member.dedicacion_fte === 1.0) return 'Full-Time'
  if (member.dedicacion_fte === 0.5) return 'Part-Time'
  if (member.dedicacion_fte === 0.25) return 'Quarter-Time'
  return `${member.horas_custom}h/mes`
}

/**
 * Margin color class for profitability dashboard
 */
export function marginClass(mg: number): string {
  if (mg >= 50) return 'margin-excellent'
  if (mg >= 35) return 'margin-good'
  if (mg >= 20) return 'margin-warning'
  return 'margin-danger'
}

/**
 * Sanitize HTML to prevent XSS when using dangerouslySetInnerHTML
 * Strips script tags, event handlers, and other dangerous patterns
 */
export function sanitizeHtml(html: string): string {
  if (typeof window === 'undefined') return html
  const div = document.createElement('div')
  div.innerHTML = html
  // Remove all script tags
  div.querySelectorAll('script').forEach(el => el.remove())
  // Remove event handler attributes
  div.querySelectorAll('*').forEach(el => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name)
    }
    // Remove javascript: URLs
    if (el.getAttribute('href')?.startsWith('javascript:')) el.removeAttribute('href')
    if (el.getAttribute('src')?.startsWith('javascript:')) el.removeAttribute('src')
  })
  return div.innerHTML
}

/**
 * Genera el siguiente número INTERNO de factura (FAC-NNNNNN). Es un correlativo de
 * trabajo, NO un comprobante fiscal: al emitir a SUNAT, NubeFact reemplaza el número
 * con la serie/correlativo reales. Antes usaba el prefijo "F001-", indistinguible de
 * una serie SUNAT real (auditoría FIS-02).
 */
export function nextFacturaNumber(existingNumbers: string[]): string {
  const nums = existingNumbers.map(n => {
    const match = /^FAC-(\d+)/.exec(n || '')
    return match ? parseInt(match[1]) : NaN
  }).filter(n => !isNaN(n))
  const next = nums.length === 0 ? 1 : Math.max(...nums) + 1
  return 'FAC-' + String(next).padStart(6, '0')
}

/**
 * Calcula detracción SPOT (SUNAT) para una factura.
 * Los parámetros umbral y porcentaje vienen de la tabla config_detraccion.
 * - Si moneda = PEN → totalPen = totalFactura
 * - Si moneda = USD/EUR → totalPen = r2(totalFactura * tipoCambio)
 * - Si totalPen > umbral → aplica = true, montoPen = Math.round(totalPen * porcentaje/100)
 * - Math.round() cumple la regla SUNAT: >= 0.5 → entero superior, < 0.5 → entero inferior
 */
export function calcularDetraccion(
  totalFactura: number,
  moneda: string,
  tipoCambio: number | null,
  umbralPen: number = 700,
  porcentaje: number = 12
): { aplica: boolean; totalPen: number; montoPen: number } {
  let totalPen: number
  if (moneda === 'PEN') {
    totalPen = totalFactura
  } else {
    totalPen = r2(totalFactura * (tipoCambio || 0))
  }

  if (totalPen > umbralPen) {
    return { aplica: true, totalPen, montoPen: Math.round(totalPen * porcentaje / 100) }
  }
  return { aplica: false, totalPen, montoPen: 0 }
}

/**
 * Proveedores presentes en un paquete de servicio gestionado, derivados de sus componentes.
 * Un paquete Cloud puede tener varios (Hetzner + Supabase + Cloudflare). Si no hay componentes
 * (pedidos legacy) cae al `proveedor` singular. Se usa para el badge/filtro de la tabla de Hosting.
 */
export function proveedoresDePaquete(
  p: { componentes?: { proveedor?: string }[] | null; proveedor?: string | null }
): string[] {
  const desdeComponentes = (p.componentes || [])
    .map(c => c?.proveedor)
    .filter((v): v is string => !!v)
  const unicos = Array.from(new Set(desdeComponentes))
  return unicos.length > 0 ? unicos : [p.proveedor || 'verpex_whm']
}

/**
 * Export an array of plain objects to an .xlsx file (client-side, no backend).
 * @param rows      Array of objects — each key becomes a column header
 * @param filename  Filename without extension (e.g. "Tareas-Proyecto-X")
 * @param sheetName Optional sheet name (defaults to "Datos")
 */
export async function exportToExcel(
  rows: Record<string, unknown>[],
  filename: string,
  sheetName = 'Datos'
): Promise<void> {
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, `${filename}.xlsx`)
}

/**
 * Generate a blank Excel template with the given headers and download it.
 * Used for import templates so users know which columns to fill.
 */
export async function downloadExcelTemplate(
  headers: string[],
  filename: string
): Promise<void> {
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.aoa_to_sheet([headers])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Plantilla')
  XLSX.writeFile(wb, `${filename}.xlsx`)
}

/**
 * PRINCIPAL -> principal, FACTURACION -> facturacion. Las clases `.rol-chip--*`
 * de index.css van sin tildes, asi que el nombre del rol se normaliza antes de
 * componerlas. Fuente unica: lo usan la ficha del contacto y el panel del lead.
 */
export function slugRol(rol: string): string {
  return rol.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}
